import { Slot } from '@radix-ui/react-slot';
import * as React from 'react';

import { VariantProps, cn, tv } from './cn';

const buttonVariants = tv({
  base: 'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-bg-secondary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-accent-quaternary focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-10',
  variants: {
    variant: {
      default:
        'bg-bg-accent-strong-secondary text-bg-primary hover:bg-bg-accent-strong-secondary/80',
      dark: 'bg-text-accent-primary text-bg-primary hover:bg-text-accent-primary/80',
      secondary:
        'border-none bg-bg-accent-tertiary text-bg-accent-strong-primary hover:bg-bg-accent-quaternary hover:text-bg-accent-strong-primary',
      destructive:
        'bg-bg-negative-strong-primary text-text-negative-strong hover:bg-bg-negative-strong-primary/90',
      outline:
        'border border-border-primary bg-bg-primary shadow-sm hover:text-text-primary',
      ghost:
        'hover:bg-bg-accent-strong-secondary hover:text-text-accent-strong',
      link: 'text-bg-accent-strong-primary underline-offset-4 hover:underline',
      gray: 'bg-text-primary text-white hover:bg-text-primary/90',
      lightgray:
        'border border-border-primary bg-bg-secondary text-text-secondary',
      toastprimary:
        'w-auto min-w-[100px] whitespace-nowrap bg-text-accent-primary px-4 text-xs text-bg-primary hover:bg-text-accent-primary/80',
      toastsecondary:
        'w-auto min-w-[100px] whitespace-nowrap border border-border-primary bg-bg-primary px-4 text-xs text-text-secondary hover:text-text-primary',
      outlinesecondary:
        'border border-border-primary bg-bg-secondary hover:text-text-primary',
    },
    size: {
      default: 'h-10 px-4 py-2',
      xs: 'h-7 rounded px-2',
      sm: 'h-9 rounded-md px-3',
      lg: 'h-11 rounded-md px-8',
      icon: 'size-10',
      smallIcon: 'size-8',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'default',
  },
});

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
